import React, { useState, useEffect } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

// Add newline here to separate external from internal
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";

// Actual form component that uses Stripe Elements
function CheckoutForm({
  listing,
  clientSecret,
  onPaymentSuccess,
  onPaymentError,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!stripe || !clientSecret) {
      return;
    }
  }, [stripe, clientSecret]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      console.log("Stripe.js hasn't loaded yet.");
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    const confirmResult = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/stripe-onboarding/return`,
      },
      redirect: "if_required",
    });

    if (confirmResult.error) {
      console.error("Stripe confirmPayment error:", confirmResult.error);
      setMessage(
        confirmResult.error.message || "An unexpected error occurred."
      );
      onPaymentError(
        confirmResult.error.message || "An unexpected error occurred."
      );
    } else if (confirmResult.paymentIntent) {
      console.log("PaymentIntent status:", confirmResult.paymentIntent.status);
      if (
        confirmResult.paymentIntent.status === "succeeded" ||
        confirmResult.paymentIntent.status === "processing"
      ) {
        onPaymentSuccess(confirmResult.paymentIntent);
      } else if (
        confirmResult.paymentIntent.status === "requires_payment_method"
      ) {
        setMessage("Payment failed. Please try another payment method.");
        onPaymentError("Payment failed. Please try another payment method.");
      } else {
        setMessage("Payment status: " + confirmResult.paymentIntent.status);
        onPaymentError("Payment status: " + confirmResult.paymentIntent.status);
      }
    }

    setIsProcessing(false);
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Complete Your Purchase</CardTitle>
        <CardDescription>
          You are buying: {listing?.title || "Item"} for $
          {listing?.price ? Number(listing.price).toFixed(2) : "N/A"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <PaymentElement options={{ layout: "tabs" }} />
          <Button
            type="submit"
            disabled={!stripe || !elements || isProcessing}
            className="w-full mt-4"
          >
            {isProcessing
              ? "Processing..."
              : `Pay $${listing?.price ? Number(listing.price).toFixed(2) : "N/A"}`}
          </Button>
          {message && (
            <div id="payment-message" className="text-red-500 text-sm mt-2">
              {message}
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// Need to wrap the actual usage in CheckoutPage with Elements provider
// This component assumes it's rendered within an Elements context

// Modified component to receive clientSecret via props
function CheckoutFormWrapper(props) {
  if (!props.clientSecret) {
    return <div>Loading payment details...</div>;
  }

  return <CheckoutForm {...props} />;
}

export default CheckoutFormWrapper;
